/**
 * projectionsModalPreferences.ts – the projections modal's two header toggles, remembered on the
 * device: "financial year / calendar year" and "future dollars / today's dollars".
 *
 * These are reading preferences, not properties of a client file or a strategy — whichever way
 * someone likes to read a projection, they like it that way every time they open one. So the modal
 * owns both toggles and they persist here, rather than being handed in by whoever opens the modal
 * (which reset them to the caller's default on every open).
 *
 * AgedCareAnalyser reads the period mode too: it, not the modal, runs the engine, and calendar-year
 * rows are a separate run. Both sides read this one store, so they open in step with no flash of
 * financial-year rows under calendar-year headings.
 *
 * Every access is wrapped — localStorage throws in some private-browsing modes, and a preference
 * that can't be stored must simply fall back to the default, never break the modal.
 */

/** The two period modes the modal offers — the engine's third (`'strategy_year'`) has no UI. */
export type ProjectionsModalPeriodMode = 'financial_year' | 'calendar_year';

const PERIOD_MODE_KEY = 'awiseProjectionsPeriodMode';
const TODAYS_DOLLARS_KEY = 'awiseProjectionsTodaysDollars';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the preference just won't stick */
  }
}

/**
 * The remembered period mode. Defaults to financial year — how the engine itself models, and the
 * cheaper of the two (calendar-year rows need their own engine run). Anything unrecognised in
 * storage reads as the default rather than being trusted through to the engine.
 */
export function readPeriodMode(): ProjectionsModalPeriodMode {
  return readStored(PERIOD_MODE_KEY) === 'calendar_year' ? 'calendar_year' : 'financial_year';
}

export function writePeriodMode(mode: ProjectionsModalPeriodMode): void {
  writeStored(PERIOD_MODE_KEY, mode);
}

/** The remembered dollars basis: true = deflate to today's dollars, false (default) = nominal future dollars. */
export function readTodaysDollars(): boolean {
  return readStored(TODAYS_DOLLARS_KEY) === '1';
}

export function writeTodaysDollars(todaysDollars: boolean): void {
  writeStored(TODAYS_DOLLARS_KEY, todaysDollars ? '1' : '0');
}
